/**
 * RaceDebugOverlayState — module singleton holding the "show GM Race debug
 * overlay" toggle. Follows the same React/dev-action → renderer bridge
 * pattern as AggroRangeOverlayState: the DebugToolbox toggle writes via the
 * setter; the RaceDebugOverlay widget + any renderer reads
 * isRaceDebugOverlayEnabled() each frame.
 *
 * Session-scoped — initializes from RACE_DEBUG.defaultEnabled (ON while the
 * GM Race arc is in active playtesting) on every load.
 *
 * Also holds a rolling event log of recent race:* + gmEvent* events for the
 * overlay's event-log section.
 */

import { RACE_DEBUG } from '../../../shared/config/race-debug.config';

let enabled = RACE_DEBUG.defaultEnabled;

export function setRaceDebugOverlayEnabled(value: boolean): void {
  enabled = value;
}

export function isRaceDebugOverlayEnabled(): boolean {
  return enabled;
}

/** Flip the overlay; returns the new state (for logging the toggle). */
export function toggleRaceDebugOverlay(): boolean {
  enabled = !enabled;
  return enabled;
}

// ── In-world render toggles ──────────────────────────────────────────

let renderHitboxes = RACE_DEBUG.renderHitboxes.defaultEnabled;
let renderPowerupBoxes = RACE_DEBUG.renderPowerupBoxes.defaultEnabled;
let renderCheckpoints = RACE_DEBUG.renderCheckpoints.defaultEnabled;

export function isRenderHitboxesEnabled(): boolean { return renderHitboxes; }
export function isRenderPowerupBoxesEnabled(): boolean { return renderPowerupBoxes; }
export function isRenderCheckpointsEnabled(): boolean { return renderCheckpoints; }

export function setRenderHitboxes(v: boolean): void { renderHitboxes = v; }
export function setRenderPowerupBoxes(v: boolean): void { renderPowerupBoxes = v; }
export function setRenderCheckpoints(v: boolean): void { renderCheckpoints = v; }

export function toggleRenderHitboxes(): boolean { renderHitboxes = !renderHitboxes; return renderHitboxes; }
export function toggleRenderPowerupBoxes(): boolean { renderPowerupBoxes = !renderPowerupBoxes; return renderPowerupBoxes; }
export function toggleRenderCheckpoints(): boolean { renderCheckpoints = !renderCheckpoints; return renderCheckpoints; }

// ── Rolling event log ────────────────────────────────────────────────

export interface RaceDebugLogEntry {
  ts: number;
  type: string;
  summary: string;
}

const log: RaceDebugLogEntry[] = [];
const maxEntries = RACE_DEBUG.eventLogMaxEntries;

export function pushRaceDebugEvent(type: string, summary: string): void {
  if (!enabled) return;
  log.push({ ts: Date.now(), type, summary });
  if (log.length > maxEntries) log.shift();
}

export function getRaceDebugLog(): RaceDebugLogEntry[] {
  return log;
}

export function clearRaceDebugLog(): void {
  log.length = 0;
}
